<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import {
    ChevronDown,
    ChevronRight,
    Download,
    FileSpreadsheet,
    FileText,
    HelpCircle,
    LogOut,
    User,
    Users,
    Sun,
    Moon
  } from 'lucide-svelte';
  import { theme, toggleTheme } from '../../theme';
  import { router, goAdminUsers } from '$lib/router';
  import { uiState } from '../../state/ui.svelte';
  import { downloadMappings, downloadAllFormats } from '../../export/firmware';
  import { projectsQuery } from '../../queries/projects';
  import { profileQuery } from '../../queries/profile';
  import { unitsQuery } from '../../queries/units';
  import { workspaceMode, setWorkspaceMode } from '../../workspaceMode';
  import {
    downloadLogicSheet,
    downloadLoadChart,
    exportElectricalLayout,
    downloadEngravingSheet,
    downloadAddressSchedule
  } from '../../unitExports';

  let {
    onNavigate = (_view: string) => {},
    onLogout = () => {}
  }: {
    onNavigate?: (view: string) => void;
    onLogout?: () => void;
  } = $props();

  const projects = projectsQuery();
  const units = unitsQuery();
  const profile = profileQuery();

  let isAdmin = $derived(profile.data?.user_role === 'admin');
  let route = $derived(router.route);

  let project = $derived(
    (projects.data ?? []).find((p) => p.id === router.projectId) ?? null
  );
  let unit = $derived((units.data ?? []).find((u) => u.id === router.unitId) ?? null);

  let showExportMenu = $state(false);
  let showUserMenu = $state(false);
  let username = '';

  function openCommandPalette() {
    document.dispatchEvent(new CustomEvent('open-command-palette'));
  }

  function closeMenus() {
    showExportMenu = false;
    showUserMenu = false;
  }

  function handleDocumentClick() {
    closeMenus();
  }

  onMount(() => {
    username = sessionStorage.getItem('username') || 'User';
    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('open-export-menu', handleOpenExportMenu);
  });

  onDestroy(() => {
    document.removeEventListener('click', handleDocumentClick);
    document.removeEventListener('open-export-menu', handleOpenExportMenu);
  });

  function handleOpenExportMenu() {
    showUserMenu = false;
    setTimeout(() => {
      showExportMenu = true;
    }, 0);
  }
</script>

<header
  class="h-10 shrink-0 bg-bg-base border-b border-border-default flex items-center px-2 sm:px-3 gap-2 sm:gap-3"
>
  <button
    type="button"
    onclick={() => onNavigate('dashboard')}
    class="flex items-center shrink-0"
    aria-label="Go to dashboard"
  >
    <img src="/images/toyama-logo.svg" alt="Toyama" class="h-4" />
  </button>

  <div class="flex items-center gap-1.5 text-sm text-text-secondary min-w-0 flex-1">
    {#if route.name === 'dashboard'}
      <span class="text-text-primary font-medium">Dashboard</span>
    {:else if route.name === 'guide'}
      <button
        type="button"
        class="hover:text-text-primary transition-colors duration-100"
        onclick={() => onNavigate('dashboard')}
      >
        Dashboard
      </button>
      <ChevronRight class="w-3.5 h-3.5 shrink-0" />
      <span class="text-text-primary font-medium truncate">Help & Guide</span>
    {:else if route.name === 'admin-users'}
      <button
        type="button"
        class="hover:text-text-primary transition-colors duration-100"
        onclick={() => onNavigate('dashboard')}
      >
        Dashboard
      </button>
      <ChevronRight class="w-3.5 h-3.5 shrink-0" />
      <span class="text-text-primary font-medium truncate">Users</span>
    {:else if route.name === 'project' && project}
      <button
        type="button"
        class="hover:text-text-primary transition-colors duration-100"
        onclick={() => onNavigate('dashboard')}
      >
        Dashboard
      </button>
      <ChevronRight class="w-3.5 h-3.5 shrink-0" />
      <span class="text-text-primary font-medium truncate">{project.name}</span>
    {:else if route.name === 'unit' && project && unit}
      <button
        type="button"
        class="hover:text-text-primary transition-colors duration-100 truncate"
        onclick={() => onNavigate('project')}
      >
        {project.name}
      </button>
      <ChevronRight class="w-3.5 h-3.5 shrink-0" />
      <span class="text-text-primary font-medium truncate">{unit.name}</span>
    {/if}
  </div>

  {#if route.name === 'unit' && unit}
    <div
      class="inline-flex rounded border border-border-default p-0.5 text-xs shrink-0"
      role="group"
      aria-label="Workspace mode"
    >
      <button
        type="button"
        class="px-2.5 py-1 rounded transition-colors duration-100 {workspaceMode === 'design'
          ? 'bg-text-primary text-bg-base'
          : 'bg-bg-hover text-text-secondary hover:text-text-primary'}"
        onclick={() => setWorkspaceMode('design')}
      >
        Design
      </button>
      <button
        type="button"
        class="px-2.5 py-1 rounded transition-colors duration-100 {workspaceMode ===
        'commissioning'
          ? 'bg-text-primary text-bg-base'
          : 'bg-bg-hover text-text-secondary hover:text-text-primary'}"
        onclick={() => setWorkspaceMode('commissioning')}
      >
        Commissioning
      </button>
    </div>

    <div class="relative shrink-0">
      <button
        type="button"
        class="inline-flex items-center gap-1 px-2 py-1 text-xs text-text-secondary hover:text-text-primary hover:bg-bg-hover rounded transition-colors duration-100"
        onclick={(e) => {
          e.stopPropagation();
          showUserMenu = false;
          showExportMenu = !showExportMenu;
        }}
        disabled={uiState.isExporting}
      >
        <Download class="w-3.5 h-3.5" />
        Export
        <ChevronDown class="w-3 h-3" />
      </button>

      {#if showExportMenu}
        <div
          class="absolute right-0 top-full mt-1 w-52 bg-bg-canvas border border-border-strong rounded shadow-toyama-md z-50 py-1 pop-in origin-top-right"
        >
          <button
            type="button"
            class="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-text-primary hover:bg-bg-hover text-left"
            onclick={() => {
              downloadLogicSheet();
              closeMenus();
            }}
          >
            <FileText class="w-3.5 h-3.5" />
            Logic Sheet (PDF)
          </button>
          <button
            type="button"
            class="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-text-primary hover:bg-bg-hover text-left"
            onclick={() => {
              downloadLoadChart();
              closeMenus();
            }}
          >
            <FileSpreadsheet class="w-3.5 h-3.5" />
            Load Chart (PDF)
          </button>
          <button
            type="button"
            class="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-text-primary hover:bg-bg-hover text-left"
            onclick={() => {
              exportElectricalLayout();
              closeMenus();
            }}
          >
            <FileText class="w-3.5 h-3.5" />
            Electrical Layout (PDF)
          </button>
          <button
            type="button"
            class="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-text-primary hover:bg-bg-hover text-left"
            onclick={() => {
              downloadEngravingSheet();
              closeMenus();
            }}
          >
            <FileText class="w-3.5 h-3.5" />
            Engraving Sheet (PDF)
          </button>
          <button
            type="button"
            class="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-text-primary hover:bg-bg-hover text-left"
            onclick={() => {
              downloadAddressSchedule();
              closeMenus();
            }}
          >
            <FileSpreadsheet class="w-3.5 h-3.5" />
            Address Schedule (CSV)
          </button>
          <div class="my-1 border-t border-border-default"></div>
          <button
            type="button"
            class="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-text-primary hover:bg-bg-hover text-left"
            onclick={() => {
              downloadMappings('v3');
              closeMenus();
            }}
            disabled={uiState.isExporting}
          >
            V3 Mappings
          </button>
          <button
            type="button"
            class="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-text-primary hover:bg-bg-hover text-left"
            onclick={() => {
              downloadMappings('v2_json');
              closeMenus();
            }}
            disabled={uiState.isExporting}
          >
            V2 Mappings
          </button>
          <button
            type="button"
            class="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-text-primary hover:bg-bg-hover text-left"
            onclick={() => {
              downloadMappings('v2_header');
              closeMenus();
            }}
            disabled={uiState.isExporting}
          >
            Header File
          </button>
          <div class="my-1 border-t border-border-default"></div>
          <button
            type="button"
            class="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-accent-on-primary bg-accent-primary/90 hover:bg-accent-primary text-left font-medium"
            onclick={() => {
              downloadAllFormats();
              closeMenus();
            }}
            disabled={uiState.isExporting}
          >
            All Formats (ZIP)
          </button>
        </div>
      {/if}
    </div>
  {/if}

  <button
    type="button"
    class="inline-flex items-center justify-center h-6 w-6 text-text-muted hover:text-text-primary hover:bg-bg-hover rounded transition-colors duration-100 shrink-0"
    onclick={toggleTheme}
    title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
    aria-label="Toggle theme"
  >
    {#if theme === 'dark'}
      <Sun class="w-3.5 h-3.5" />
    {:else}
      <Moon class="w-3.5 h-3.5" />
    {/if}
  </button>

  <button
    type="button"
    class="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-2xs text-text-muted border border-border-default rounded bg-bg-hover hover:bg-bg-selected transition-colors duration-100 shrink-0"
    onclick={openCommandPalette}
    title="Command palette"
  >
    <span class="font-mono">⌘R</span>
  </button>

  <div class="relative shrink-0">
    <button
      type="button"
      class="inline-flex items-center gap-1.5 px-2 py-1 text-xs text-text-secondary hover:text-text-primary hover:bg-bg-hover rounded transition-colors duration-100 max-w-[160px]"
      onclick={(e) => {
        e.stopPropagation();
        showExportMenu = false;
        showUserMenu = !showUserMenu;
      }}
    >
      <User class="w-3.5 h-3.5 shrink-0" />
      <span class="truncate">{username}</span>
      <ChevronDown class="w-3 h-3 shrink-0" />
    </button>

    {#if showUserMenu}
      <div
        class="absolute right-0 top-full mt-1 w-44 bg-bg-canvas border border-border-strong rounded shadow-toyama-md z-50 py-1 pop-in origin-top-right"
      >
        <button
          type="button"
          class="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-text-primary hover:bg-bg-hover text-left"
          onclick={() => {
            onNavigate('guide');
            closeMenus();
          }}
        >
          <HelpCircle class="w-3.5 h-3.5" />
          Help & Guide
        </button>
        {#if isAdmin}
          <button
            type="button"
            class="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-text-primary hover:bg-bg-hover text-left"
            onclick={() => {
              goAdminUsers();
              closeMenus();
            }}
          >
            <Users class="w-3.5 h-3.5" />
            Manage Users
          </button>
        {/if}
        <button
          type="button"
          class="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-text-primary hover:bg-bg-hover text-left"
          onclick={() => {
            closeMenus();
            onLogout();
          }}
        >
          <LogOut class="w-3.5 h-3.5" />
          Logout
        </button>
      </div>
    {/if}
  </div>
</header>
